/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useConsent } from "@packages/consent";
import type { PasswordAuthFlowForm } from "@packages/sdk";
import {
  preexistingPasswordAuthFlowSchema,
  useRequestAuth,
} from "@packages/sdk";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { standardSchemaResolver } from "../../../reactHookForm";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { EyeIcon } from "../../_base/icons/Icon/GeneralIcons";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  [cssMediaQueryMinWidthTablet]: { justifyContent: "flex-end" },
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const PasswordVisibilityButton = ({
  onClick,
  visible,
}: {
  onClick: () => void;
  visible: boolean;
}) => {
  return (
    <IconButton
      icon={visible ? <EyeIcon.Off /> : <EyeIcon.On />}
      onClick={onClick}
      type="button"
      size="m"
      variant="transparentPrimary"
    />
  );
};

type PasswordAuthFlowProps = AuthFlowProps & {
  type?: "standard" | "student";
};

export const PasswordAuthFlow = ({
  onAuth,
  setFlow,
  state,
  utm,
  onFormSubmit,
  onError,
  formDisabled = false,
  hideHeader,
  className = "",
  layoutCSS = {},
  type = "standard",
}: PasswordAuthFlowProps) => {
  const t = useTranslations();
  const alert = useAlert();
  const { mutationLogin, mutationUsernameLogin, mutationForgotPassword } =
    useRequestAuth();
  const [visible, setVisible] = useState(false);
  const { email, login } = state;
  const { voluntaryConsent } = useConsent();
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<PasswordAuthFlowForm>(
      standardSchemaResolver(preexistingPasswordAuthFlowSchema),
    );
  const { track } = useAnalytics();

  useEffect(() => {
    if (type === "standard" && !email) setFlow("email");
    else if (type === "student" && !login) setFlow("usernameOrEmail");
  }, [email, login, type]);

  const form = useForm<PasswordAuthFlowForm>({ resolver });
  const isDesktop = useIsDesktopViewport();

  const onSubmit = async ({ password }: PasswordAuthFlowForm) => {
    onFormSubmit?.({ email: email ?? login, ...utm });
    try {
      if (type === "student") {
        const isEmail = z.email().safeParse(login).success;
        const data = isEmail
          ? await mutationLogin.mutateAsync({ email: login, password })
          : await mutationUsernameLogin.mutateAsync({
              username: login,
              password,
            });
        await onAuth?.({ data });
        return;
      }
      const data = await mutationLogin.mutateAsync({
        email,
        password,
        consent: voluntaryConsent ?? undefined,
      });
      await onAuth?.({ data });
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description:
          [1000, 1031].includes(error.code) && type === "student"
            ? t("student_password_account_auth_error_message")
            : error.message,
      });
      onError?.(error);
    }
  };

  const handlePasswordReset = async () => {
    try {
      await mutationForgotPassword.mutateAsync({ email });

      await alert.show({
        title: t("general_word_success_exclamation"),
        description: t("dialog_reset_password_email_sent_message", {
          0: email,
        }),
      });
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  // if state isn't loaded properly yet, don't show broken stuff
  if (type === "standard" && !email) return null;
  if (type === "student" && !login) return null;

  return (
    <AuthForm<PasswordAuthFlowForm>
      form={form}
      onSubmit={onSubmit}
      className={className}
      footer={(_, disabled) => (
        <div
          key={"buttons"}
          css={[buttonsStyle, type === "student" ? justOneStyle : null]}
        >
          {type === "student" ? null : (
            <Button
              isFullWidth={!isDesktop}
              size="m"
              variant="transparentPrimary"
              type="button"
              onClick={() => handlePasswordReset()}
            >
              {t("password_input_help_option1")}
            </Button>
          )}
          <AuthSubmitButton disabled={disabled || formDisabled}>
            <I18nMessage message="login_title" />
          </AuthSubmitButton>
        </div>
      )}
      layoutCSS={layoutCSS}
    >
      {(form) => (
        <>
          {!hideHeader && (
            <EmailHeader
              title={t("login_title")}
              onBack={() => {
                track({
                  event: HallowAnalyticsEvent.TappedBack,
                  properties: { screen_name: "password_input" },
                });
                setFlow(type === "student" ? "usernameOrEmail" : "email");
              }}
            />
          )}
          <Text type="headline" size="m" as="h2">
            {t("password_input_headline_existing")}
          </Text>
          <input type="hidden" name="email" value={email} />
          <FormField
            control={form.control}
            name="password"
            showErrorMessage={true}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  type={visible ? "text" : "password"}
                  required
                  autoFocus
                  autoCorrect="off"
                  autoComplete={"current-password"}
                  placeholder={t("auth_enter_login_password")}
                  button={
                    <PasswordVisibilityButton
                      visible={visible}
                      onClick={() => setVisible(!visible)}
                    />
                  }
                  disabled={formDisabled}
                  {...inputProps}
                  css={fieldsCSS.password}
                  {...field}
                />
                <AuthError css={errorsCSS.password}>
                  {errors?.password?.message ? t(errors.password.message) : ""}
                </AuthError>
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};

PasswordAuthFlow.displayName = "PasswordAuthFlow";
